import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, ESITO_META, EsitoBadge, bboxShort, cropUrl } from "./primitives";

/** Ritaglio ad alta risoluzione del PDF originale intorno a un'evidenza (sole letture). */
export const CropDialog = ({ crop, onClose }) => (
  <Dialog open={!!crop} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-[95vw] sm:max-w-4xl text-xs" data-testid="cl-crop-dialog">
      <DialogHeader><DialogTitle className="text-sm">Ritaglio PDF originale — {crop?.cid} · pagina {crop?.ev?.page || 1} · {crop?.campo}</DialogTitle></DialogHeader>
      {crop && (
        <div className="space-y-2">
          <div className="text-[10px] text-muted-foreground">Evidenza {crop.ev.segment_id}:{crop.ev.evidence_id || "bbox feature"} · bbox {bboxShort(crop.ev.bbox_pct || crop.ev.feature_bbox_pct)} · testo letto: «{crop.ev.text || "—"}»</div>
          <div className="border border-border bg-white overflow-auto max-h-[70vh]">
            <img src={cropUrl(crop.cid, crop.ev, 260)} alt={`ritaglio ${crop.cid}`} className="max-w-full" data-testid="cl-crop-image" />
          </div>
          <div className="text-[10px] text-muted-foreground">FASE A: «{crop.fase_a}» · lettore: «{crop.lettore}» · esito {crop.esito}{crop.nota ? ` · ${crop.nota}` : ""}</div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const EvidenceCell = ({ row, onCrop }) => {
  const evs = row.evidence || [];
  if (!evs.length) return <span className="text-muted-foreground" title="nessun riferimento strutturato (source_refs) nel risultato fuso per questo campo">n.d.</span>;
  return (
    <div className="space-y-0.5">
      {evs.slice(0, 3).map((e, i) => (
        <div key={i} className="whitespace-nowrap">
          <span className="font-mono">{e.segment_id}:{e.evidence_id || "bbox"}</span> p.{e.page || "—"} <span className="text-muted-foreground">{bboxShort(e.bbox_pct || e.feature_bbox_pct)}</span>
          {(e.bbox_pct || e.feature_bbox_pct) && <button className="ml-1 link-tech hover:underline" onClick={() => onCrop({ cid: row.case_id, ev: e, campo: row.campo, fase_a: row.fase_a, lettore: row.lettore, esito: row.esito, nota: row.nota })} data-testid={`cl-crop-${row.case_id}-${row.idx}-${i}`}>ritaglio</button>}
        </div>
      ))}
      {evs.length > 3 && <div className="text-muted-foreground">+{evs.length - 3}</div>}
    </div>
  );
};

/** Tabella completa dei dati confrontati con filtri (caso, esito, impatto sul preventivo). */
export const FieldsTable = ({ rows, cases, embedded = false, defaultCase = "", readerLabel = "Lettore (primo risultato congelato)" }) => {
  const [fCase, setFCase] = useState(defaultCase);
  const [fEsito, setFEsito] = useState("");
  const [onlyImpact, setOnlyImpact] = useState(false);
  const [crop, setCrop] = useState(null);
  const filtered = useMemo(() => rows.filter((r) => (!fCase || r.case_id === fCase) && (!fEsito || r.esito === fEsito) && (!onlyImpact || r.quote_impact)), [rows, fCase, fEsito, onlyImpact]);
  const hasRev = rows.some((r) => r.revisions?.length);
  const body = (
    <>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        {!embedded && (
          <select value={fCase} onChange={(e) => setFCase(e.target.value)} className="border border-border px-1 py-0.5" data-testid="cl-filter-case">
            <option value="">Tutti i casi</option>{cases.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select value={fEsito} onChange={(e) => setFEsito(e.target.value)} className="border border-border px-1 py-0.5" data-testid="cl-filter-esito">
          <option value="">Tutti gli esiti</option>{Object.entries(ESITO_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <label className="flex items-center gap-1"><input type="checkbox" checked={onlyImpact} onChange={(e) => setOnlyImpact(e.target.checked)} data-testid="cl-filter-impact" />solo differenze che incidono sul preventivo</label>
        <span className="text-muted-foreground" data-testid="cl-fields-count">{filtered.length} / {rows.length} dati</span>
      </div>
      <div className="table-scroll">
        <table className="w-full text-[10px]" data-testid="cl-fields-table">
          <thead><tr className="bg-muted text-left">
            {["Caso", "Campo", "FASE A (verità tecnica)", readerLabel, "Esito", "Preventivo", "Nota", "Evidenze (pagina · bbox %)", ...(hasRev ? ["Rilettura post-correzione"] : [])].map((h) => <th key={h} className="px-2 py-1 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r.case_id}-${r.idx}`} className="border-t border-border/60 align-top" data-testid={`cl-field-${r.case_id}-${r.idx}`}>
                <td className="px-2 py-1 font-bold whitespace-nowrap">{r.case_id}</td>
                <td className="px-2 py-1 whitespace-nowrap">{r.campo}</td>
                <td className="px-2 py-1 max-w-[220px]">{r.fase_a}</td>
                <td className="px-2 py-1 max-w-[260px]">{r.lettore}</td>
                <td className="px-2 py-1"><EsitoBadge esito={r.esito} testId={`cl-esito-${r.case_id}-${r.idx}`} /></td>
                <td className="px-2 py-1">{r.quote_impact ? <span className="font-bold text-risk-800">incide</span> : "—"}</td>
                <td className="px-2 py-1 max-w-[260px] text-muted-foreground">{r.nota}</td>
                <td className="px-2 py-1"><EvidenceCell row={r} onCrop={setCrop} /></td>
                {hasRev && <td className="px-2 py-1 max-w-[260px]" data-testid={`cl-rev-${r.case_id}-${r.idx}`}>{(r.revisions || []).map((v, i) => (
                  <div key={i} className={v.changed ? "font-bold" : "text-muted-foreground"}>{v.revision_id}: {v.lettore} → <EsitoBadge esito={v.esito} />{v.nota ? <span className="text-muted-foreground"> · {v.nota}</span> : null}</div>
                ))}{!(r.revisions || []).length && <span className="text-muted-foreground">non riletto</span>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CropDialog crop={crop} onClose={() => setCrop(null)} />
    </>
  );
  if (embedded) return <div className="space-y-2">{body}</div>;
  return <Card title={`Tabella dei ${rows.length} dati confrontati (FASE A vs primo risultato)`} subtitle="Evidenze = riferimenti strutturati (source_refs / bbox) del risultato fuso; «ritaglio» apre il PDF originale ad alta risoluzione. Nessun dato è stato ricalcolato." testId="cl-fields">{body}</Card>;
};
